/**
 * textToVideo.js — turn a caption into a short vertical MP4-ready WebM clip.
 *
 * Public API:
 *   generateTextVideoBlob(text, opts?) → Promise<Blob>
 *
 * The clip animates the text on top of a Caribbean-flag inspired gradient
 * (green→gold→black). Text auto-wraps to fit the vertical 720x1280 canvas
 * used by TikTok-style feeds. This is a smaller, no-UI counterpart to
 * `/studio` — meant for the "just type something and post" flow on /create.
 */
import { recordVideo } from "./videoExporter";

const DEFAULT_SCHEME = {
  bg: "linear-gradient(135deg, #009b3a 0%, #fed100 45%, #0a0a0a 100%)",
  text: "#ffffff",
};

function extractColorStops(bg) {
  return bg.match(/#[0-9a-f]{3,6}/gi) || ["#000", "#000"];
}

function wrapLines(ctx, text, maxWidth) {
  const paragraphs = text.split(/\n+/);
  const lines = [];
  paragraphs.forEach((p) => {
    if (!p.trim()) { lines.push(""); return; }
    const words = p.split(/\s+/);
    let line = "";
    words.forEach((word) => {
      const test = line ? `${line} ${word}` : word;
      if (ctx.measureText(test).width > maxWidth && line) {
        lines.push(line);
        line = word;
      } else {
        line = test;
      }
    });
    if (line) lines.push(line);
  });
  return lines.slice(0, 8);
}

function drawFrame(canvas, elapsed, duration, text, scheme = DEFAULT_SCHEME) {
  const ctx = canvas.getContext("2d");
  const W = canvas.width;
  const H = canvas.height;
  const progress = Math.max(0, Math.min(1, elapsed / Math.max(0.001, duration)));

  // Animated diagonal gradient — subtly drifts across the clip's lifetime.
  const drift = Math.sin(progress * Math.PI * 2) * 60;
  const grad = ctx.createLinearGradient(drift, 0, W - drift, H);
  const stops = extractColorStops(scheme.bg);
  stops.forEach((c, i) => grad.addColorStop(i / Math.max(1, stops.length - 1), c));
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);

  // Vignette
  const vignette = ctx.createRadialGradient(W / 2, H / 2, W * 0.2, W / 2, H / 2, W * 0.9);
  vignette.addColorStop(0, "rgba(0,0,0,0)");
  vignette.addColorStop(1, "rgba(0,0,0,0.55)");
  ctx.fillStyle = vignette;
  ctx.fillRect(0, 0, W, H);

  // Text
  const baseFontSize = Math.min(96, Math.max(56, Math.floor(W * 0.11)));
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = scheme.text;
  ctx.font = `900 ${baseFontSize}px "Anton", "Arial Black", "Helvetica Neue", sans-serif`;
  ctx.shadowColor = "rgba(0,0,0,0.55)";
  ctx.shadowBlur = 20;

  const lines = wrapLines(ctx, text || "…", W - 160);
  const lineHeight = baseFontSize * 1.15;
  const totalHeight = lines.length * lineHeight;
  const startY = H / 2 - totalHeight / 2 + lineHeight / 2;

  // Small pulse in the second half — makes it feel alive.
  const pulse = progress > 0.6
    ? 1 + 0.03 * Math.sin((progress - 0.6) * Math.PI * 6)
    : 1;

  lines.forEach((ln, i) => {
    // Each line eases in during the first 60% of the video.
    const lineStart = (i / Math.max(1, lines.length)) * 0.6;
    const lineEnd = ((i + 1) / Math.max(1, lines.length)) * 0.6;
    const localProg = Math.max(0, Math.min(1, (progress - lineStart) / Math.max(0.001, lineEnd - lineStart)));
    const eased = 1 - Math.pow(1 - localProg, 3);
    ctx.globalAlpha = eased;
    const slideY = (1 - eased) * 40;
    const y = startY + i * lineHeight + slideY;
    ctx.save();
    ctx.translate(W / 2, y);
    ctx.scale(pulse, pulse);
    ctx.fillText(ln, 0, 0);
    ctx.restore();
  });
  ctx.globalAlpha = 1;
  ctx.shadowBlur = 0;
}

/**
 * Generate a short vertical text video for a given caption.
 * Returns a Blob (video/webm) that can be turned into a File and uploaded.
 *
 * onProgress({ phase, percent }) callback:
 *   phase = "recording" | "done" (transcoding-to-mp4 is skipped; the caller
 *   should upload WebM to Mux which handles server-side transcoding).
 */
export async function generateTextVideoBlob(text, {
  duration = 6,
  scheme = DEFAULT_SCHEME,
  onProgress,
  width = 720,
  height = 1280,
  voiceBuffer = null,
} = {}) {
  const cleanText = (text || "").trim() || "Made with Giffwurldaunce";

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const localDraw = (cv, elapsed, dur) => drawFrame(cv, elapsed, dur, cleanText, scheme);
  // Paint the first frame immediately so captureStream has something to grab
  // BEFORE MediaRecorder starts.
  localDraw(canvas, 0, duration);

  const webmBlob = await recordVideo({
    canvas,
    drawFrame: localDraw,
    duration,
    format: "webm",
    voiceBuffer,
    onProgress,
  });
  return webmBlob;
}
